"use client";

import React, { memo, useCallback, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import DashboardLayout from "@/app/components/DashboardLayout";
import LocationUpdater from "@/app/components/LocationUpdater";
import SessionProviders from "@/app/components/SessionProvider";
import ErrorBoundary from "@/app/components/ErrorBoundary";
import { TimezoneProvider } from "@/app/utils/TimezoneContext";
import VercelAnalytics from "@/lib/analytics/VercelAnalytics";
import TopLoadingProgress from "@/app/components/notifier/TopLoadingProgress";

const MemoizedDashboardLayout = memo(DashboardLayout);
const MemoizedTimezoneProvider = memo(TimezoneProvider);
const SHOW_HANDOFF_LOADING_OVERLAY = false;
const BLOCK_INTERACTION_DURING_HANDOFF = true;

export default function SharedDashboardWrapper({
  children,
  session,
  initialSetupPending = false,
  initialShowEnrichmentLoading = false,
  initialBookingHandoff = false,
}) {
  const theme = useTheme();
  const [handoffActive, setHandoffActive] = useState(Boolean(session));
  const showStartupGate = initialSetupPending && handoffActive;
  const handleHandoffChange = useCallback((active) => {
    setHandoffActive(active);
  }, []);

  return (
    <SessionProviders session={session}>
      <MemoizedTimezoneProvider>
        <ErrorBoundary>
          <LocationUpdater />
          <VercelAnalytics />
          {BLOCK_INTERACTION_DURING_HANDOFF && handoffActive && (
            <>
              <Box
                aria-hidden={!showStartupGate}
                role={showStartupGate ? "status" : undefined}
                aria-live={showStartupGate ? "polite" : undefined}
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 1299,
                  pointerEvents: "auto",
                  backgroundColor: showStartupGate || initialBookingHandoff
                    ? theme.palette.background.default
                    : SHOW_HANDOFF_LOADING_OVERLAY
                    ? theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.default, 0.42)
                      : alpha(theme.palette.background.default, 0.32)
                    : "transparent",
                  ...(SHOW_HANDOFF_LOADING_OVERLAY && {
                    backdropFilter: "blur(2px)",
                    WebkitBackdropFilter: "blur(2px)",
                  }),
                }}
              >
                {showStartupGate && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "@keyframes meex-startup-logo-pulse": {
                        "0%, 100%": { opacity: 0.55, transform: "scale(1)" },
                        "50%": { opacity: 0.9, transform: "scale(1.08)" },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={
                        theme.palette.mode === "dark"
                          ? "/images/logos/light-logo.svg"
                          : "/images/logos/dark-logo.svg"
                      }
                      alt="Meex"
                      sx={{
                        width: 48,
                        height: "auto",
                        animation:
                          "meex-startup-logo-pulse 1.8s ease-in-out infinite",
                      }}
                    />
                  </Box>
                )}
              </Box>
              {SHOW_HANDOFF_LOADING_OVERLAY && <TopLoadingProgress />}
            </>
          )}
          <MemoizedDashboardLayout
            onVerificationHandoffChange={handleHandoffChange}
            initialShowEnrichmentLoading={initialShowEnrichmentLoading}
            initialBookingHandoff={initialBookingHandoff}
          >
            {children}
          </MemoizedDashboardLayout>
        </ErrorBoundary>
      </MemoizedTimezoneProvider>
    </SessionProviders>
  );
}
